import React from "react";
import { PhoneIncoming, Sparkles, Calendar } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";
import { PoseEagle } from "../PoseEagle";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d8e47eb4-9ceb-4272-ba98-a0a7b79b0bb8/images/7d593bb6f9b893a27db19cf787d9d22772f333c1091e055ed75f00dcfd9eb81b.png";

export const Hero = () => {
  const { t } = useI18n();
  return (
    <section className="relative pt-40 lg:pt-36 pb-14 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 hero-surface">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 hero-overlay-r" />
        <div className="absolute inset-0 hero-overlay-b" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#060B19]/40 border border-white/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse-dot" />
            <span className="eyebrow">{t.hero.eyebrow}</span>
          </div>

          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.02]">
            {t.hero.title1}{t.hero.title2 && (<><br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-100 to-cyan-300">{t.hero.title2}</span></>)}
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-blue-50 leading-relaxed max-w-2xl">
            {t.hero.sub}
          </p>

          <div className="mt-10 flex items-center gap-3 text-xs text-blue-100">
            <div className="flex -space-x-1.5">
              {["🍽️","🔧","⚡","🔥","🔑"].map((e, i) => (
                <span key={i} className="w-7 h-7 rounded-full bg-[#060B19]/40 border border-white/25 flex items-center justify-center text-[11px]">{e}</span>
              ))}
            </div>
            <span>{t.hero.trust}</span>
          </div>
        </div>

        {/* Right side floating UI */}
        <div className="lg:col-span-5">
          <PoseEagle
            src="/eagle-poses/eagle-home-flying.png"
            size={250}
            mobileSize={125}
            entrance="flyin"
            testid="home-eagle"
            className="relative z-20 flex justify-center lg:justify-end lg:pr-4 mb-3"
          />
          <div className="glass-strong rounded-2xl p-5 relative animate-fade-up" style={{animationDelay:"0.15s"}} data-testid="hero-flow-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
              </div>
              <span className="text-[10px] text-slate-500 font-mono tracking-wider">TP-AGENT-01 · LIVE</span>
            </div>

            <FlowItem icon={<PhoneIncoming size={16} />} color="red" title={t.hero.flow.missed} sub={t.hero.flow.missedSub} />
            <Connector />
            <FlowItem icon={<Sparkles size={16} />} color="blue" title={t.hero.flow.ai} sub={t.hero.flow.aiSub} />
            <Connector />
            <FlowItem
              icon={<span className="font-mono text-[11px] font-bold">JS</span>}
              color="slate"
              title={t.hero.flow.reply}
              sub={t.hero.flow.replySub}
              isCustomer
            />
            <Connector />
            <FlowItem icon={<Calendar size={16} />} color="cyan" title={t.hero.flow.booked} sub={t.hero.flow.bookedSub} />

            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-xs">
              <span className="text-slate-400">{t.hero.flow.time}</span>
              <span className="text-cyan-300 font-mono font-semibold">00:00:47</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FlowItem = ({ icon, color, title, sub, isCustomer }) => {
  const colors = {
    red: "bg-red-500/15 text-red-400 border-red-500/25",
    blue: "bg-blue-500/15 text-blue-300 border-blue-500/25",
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",
    slate: "bg-slate-500/15 text-slate-300 border-slate-500/25",
  };
  return (
    <div className={`flex items-start gap-3 ${isCustomer ? "ml-6" : ""}`}>
      <div className={`shrink-0 w-9 h-9 rounded-lg border ${colors[color]} flex items-center justify-center`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-semibold leading-tight">{title}</div>
        <div className="text-xs text-slate-400 mt-1 leading-snug">{sub}</div>
      </div>
    </div>
  );
};

const Connector = () => (
  <div className="ml-[18px] my-2 h-5 border-l border-dashed border-white/15" />
);
